import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { Icon } from './Icon';
import { Text, SrOnlyText } from './Text';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../utils/styleHelpers';
import { DrawerActions, DrawerActionsProps } from '../Compounds/DetailsDrawer/DrawerActions/DrawerActions';
import { ComponentName } from '../Compounds/DetailsDrawer/detailsDrawersHelpers';

const DEFAULT_DRAWER_CLASSES = [
  'bg-white',
  'border-l',
  'border-grey-2',
  'fixed',
  'z-50',
  'flex',
  'flex-col',
  'top-0',
  'right-0',
  'h-screen',
  'lg:w-2/5',
  'w-full',
  'lg:max-w-[50rem]',
  'p-6',
  'bg-main-300'
].join(' ');

export interface DrawerProps {
  children: ReactNode;
  show: boolean;
  setShow?: (show: boolean) => void;
  title?: string;
  subtitle?: ReactNode;
  description?: string;
  className?: string;
  style?: object;
  link?: string;
  state?: object;
  componentName?: ComponentName;
  saveAction?: () => Promise<void>;
  saveDisabled?: boolean;
  showButtons?: boolean;
  data?: DrawerActionsProps['data'];
  hiddenAssetSource?: DrawerActionsProps['hiddenAssetSource'];
  onRefresh?: DrawerActionsProps['onRefresh'];
}

export const Drawer = ({
  show,
  setShow,
  title,
  subtitle,
  description,
  className,
  children,
  link = '',
  componentName,
  saveAction,
  saveDisabled,
  showButtons,
  data = null,
  hiddenAssetSource,
  onRefresh,
  ...props
}: DrawerProps) => {
  const [saving, setSaving] = useState<boolean>(false);
  const { search } = useLocation();
  if (!show) return <></>;
  return (
    <div
      {...props}
      className={`${DEFAULT_DRAWER_CLASSES} ${className}`}>
      {setShow && (
        <div className="flex items-center absolute self-end">
          {data && (
            <DrawerActions
              componentName={componentName}
              data={data}
              setShow={setShow}
              hiddenAssetSource={hiddenAssetSource}
              onRefresh={onRefresh}
            />
          )}
          <button
            data-cy="close-drawer-button"
            className="cancel-button ml-4 border w-9 h-7 rounded-lg bg-secondary-300 hover:bg-secondary-200"
            onClick={() => setShow(false)}>
            <SrOnlyText
              data-cy="cancel_button"
              dataTestId="cancel_button">
              hide drawer
            </SrOnlyText>
          </button>
        </div>
      )}
      <div className="bg-white -m-6 p-6">
        <Text
          dataCy="detail-drawer-title"
          className={`bg-card-1 w-fit ${title ? 'px-4 py-0.5 rounded-lg mb-4' : ''}`}
          color="black">
          {title}
        </Text>
        {subtitle}
        <Text
          className="mb-[15px]"
          dataCy="detail-drawer-description"
          size="sm">
          {description}
        </Text>
      </div>
      {children}
      <div className="flex items-center w-full h-[6%] pt-6">
        {link && (
          <Link
            state={props.state}
            className="flex border border-grey-500 px-2 py-[3px] text-grey-5 ml-[8px] rounded-lg"
            title="Fullscreen"
            to={`${link}${search}`}>
            <Icon
              type="expand"
              className="p-[8px] cursor-pointer"
            />
            <span className="font-sans text-slate-500 text-xs font-normal text-center pl-[3px] text-[11px]">
              Expand
            </span>
          </Link>
        )}
        {showButtons && (
          <div className="absolute right-[40px]">
            {setShow && (
              <button
                className={`${DEFAULT_CANCEL_BUTTON_STYLE} mr-[15px]`}
                onClick={() => setShow(false)}>
                Cancel
              </button>
            )}
            <button
              className={DEFAULT_ADVANCE_BUTTON_STYLE}
              disabled={saveDisabled}
              onClick={async () => {
                setSaving(true);
                await saveAction?.();
                setSaving(false);
              }}>
              {saving ? (
                <CircularProgress
                  size={12}
                  color="inherit"
                />
              ) : (
                'Save'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
